/**
 * WahaProvider — wrapper sull'esistente `wahaApiService.ts` (singleton).
 *
 * Stub no-op: NON modifica il comportamento WAHA esistente. Espone solo l'interfaccia
 * `WhatsAppProvider` per permettere al provider factory di selezionare l'implementazione
 * giusta in base a `service_name` di `api_configurations`.
 *
 * NB: i metodi Cloud-only (sendTemplate, getTemplates, getQualityRating, getCSWState)
 * NON sono implementati qui — il caller deve verificare `capabilities` o usare
 * `if (provider.sendTemplate)` prima di chiamare.
 *
 * Group operations (`getGroupParticipants`) sono delegate a wahaApiService che le supporta.
 */

import { getWahaService } from '../wahaApiService';
import type {
  WhatsAppProvider,
  WhatsAppProviderType,
  ProviderCapabilities,
  SendOptions,
  ChatOverview,
  Message,
  PresenceStatus,
  WahaSendResult,
  LoadMessagesOptions,
} from './types';

const WAHA_CAPABILITIES: ProviderCapabilities = {
  groups: true,           // WAHA supporta gruppi nativamente
  templates: false,       // No template HSM (free-form sempre)
  catalog: false,         // No catalog API
  payments: false,        // No payments
  channels: false,        // Supporto parziale, non esposto
  csw_tracking: false,    // Nessun vincolo finestra 24h (simula client mobile)
  quality_rating: false,  // No quality rating Meta
  max_throughput_mps: 10, // Limite pratico WAHA via WhatsApp Web ~10/s sostenuto
};

export class WahaProvider implements WhatsAppProvider {
  readonly type: WhatsAppProviderType = 'whatsapp';
  readonly capabilities = WAHA_CAPABILITIES;

  private get service() {
    return getWahaService();
  }

  // ─── Letture ─────────────────────────────────────────────────────────
  async getChats(limit = 50, offset = 0): Promise<ChatOverview[]> {
    return this.service.getChatsOverview(limit, offset);
  }

  async getMessages(chatId: string, opts?: LoadMessagesOptions): Promise<Message[]> {
    return this.service.getChatMessages(chatId, opts);
  }

  async getProfilePicture(chatId: string): Promise<string | null> {
    return this.service.getProfilePicture(chatId);
  }

  // ─── Invio ───────────────────────────────────────────────────────────
  async sendText(chatId: string, text: string, opts?: SendOptions): Promise<WahaSendResult> {
    return this.service.sendText(chatId, text, opts?.replyToId, opts?.mentions);
  }

  async sendImage(
    chatId: string,
    image: File,
    caption?: string,
    _opts?: SendOptions  // WAHA non supporta replyTo su media
  ): Promise<WahaSendResult> {
    return this.service.sendImage(chatId, image, caption);
  }

  async sendVideo(
    chatId: string,
    video: File,
    caption?: string,
    _opts?: SendOptions
  ): Promise<WahaSendResult> {
    return this.service.sendVideo(chatId, video, caption);
  }

  async sendFile(
    chatId: string,
    file: File,
    caption?: string,
    _opts?: SendOptions
  ): Promise<WahaSendResult> {
    return this.service.sendFile(chatId, file, caption);
  }

  async sendVoice(chatId: string, audio: Blob, _opts?: SendOptions): Promise<WahaSendResult> {
    return this.service.sendVoice(chatId, audio);
  }

  // ─── Stato chat ──────────────────────────────────────────────────────
  async markAsRead(chatId: string): Promise<void> {
    return this.service.markAsRead(chatId);
  }

  async setPresence(chatId: string, status: PresenceStatus): Promise<void> {
    return this.service.setPresence(chatId, status);
  }

  // ─── Group operations (solo WAHA) ────────────────────────────────────
  async getGroupParticipants(groupId: string): Promise<Map<string, string>> {
    return this.service.getGroupParticipants(groupId);
  }
}
